import moment from 'moment';
import tempMee from '../apis/tempMee';
import {
  GET_ALL_UNVERIFIED_LICENSES_SUCCESS,
  GET_ALL_UNVERIFIED_LICENSES_FAILURE,
  GET_ALL_UNVERIFIED_LICENSES_REQUEST,
  CREDENTIAL_ACTION,
  SHOW_MESSAGE,
  EDUCATION_ACTION,
  AUTHORIZATION_ACTION,
} from './actionTypes';

import { getAuthHeaders } from './authActions';
import { UserType } from '../enums/UserType';
import { UserProfession } from '../enums/UserProfession';
import {
  getShouldUseProtectedS3Licenses,
  getShouldUsingCredentialServiceEndpoint,
} from '../growthbook';
import {
  getCredentialsV2,
  getProtectedProfLicenses,
  patchCredentials,
  patchCredentialsV2,
  getAuthorizations,
  createAuthorization as createAuthorizationApi,
  deleteAuthorization as deleteAuthorizationApi,
  updateCredentialArtifact,
  updateCredentialNumber,
  updateCredentialReference,
  updateCredentialNpi,
  createCredentialArtifact,
  createCredentialNumber,
  createCredentialReference,
  createCredentialNpi,
} from '../services/credentials';
import { patchUser } from '../services/users';

export const fetchLicenses = (state) => async (dispatch) => {
  let params = {};

  if (state) {
    params = {
      page: state.page || 1,
      sortBy: state.sortField?.split('.')?.[1] || '',
      sortDirection: state.sortOrder?.toUpperCase() || '',
      limit: 15,
      status: state.status || '',
      state: state.state || '',
      profession: state.profession || '',
      subProfession: state.subProfession || '',
      credential_type: state.credentialType || '',
      account_status: state.accountStatus || '',
      gov_uploaded: state.govIdUploaded || '',
      gov_status: state.govIdUploaded || '',
      expiresSoon: state.expiresSoon || '',
      contractor_status: state.contractorStatus || '',
      employee_status: state.employeeStatus || '',
    };
  } else {
    params = {
      page: 1,
    };
  }

  dispatch({
    type: GET_ALL_UNVERIFIED_LICENSES_REQUEST,
    payload: params,
  });

  try {
    const headers = await getAuthHeaders();
    const response = await tempMee.get('admin/licenses', {
      params,
      headers,
    });
    dispatch({ type: GET_ALL_UNVERIFIED_LICENSES_SUCCESS, payload: response.data });
  } catch (err) {
    dispatch({ type: GET_ALL_UNVERIFIED_LICENSES_FAILURE, payload: err });
  }
};

/**
 * To get educations of professions
 *
 * @param {string[] | UserProfession[]} professions
 * @returns {(function(*): Promise<void>)|*}
 */
export const fetchRequestedEducations =
  (professions = [UserProfession.RDH, UserProfession.DA]) =>
  async (dispatch) => {
    dispatch({
      type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_REQUEST,
    });

    try {
      const headers = await getAuthHeaders();

      const responses = await Promise.all(
        professions.map((profession) =>
          tempMee.get('credential-service/credentials', {
            headers,
            params: {
              profession,
              userType: UserType.HYG,
            },
          }),
        ),
      );

      const educations = responses.reduce((acc, response) => ({
        ...acc.data,
        ...response.data,
      }));

      // eslint-disable-next-line max-len
      dispatch({ type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_SUCCESS, payload: educations });
    } catch (err) {
      dispatch({ type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_FAILURE, payload: err });
    }
  };

export const getUserEducation =
  ({ userType, profession } = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: EDUCATION_ACTION.GET_USER_EDUCATION_REQUEST });

      const user = getState().users.currentUser;
      const type = userType || user?.user_type;
      const userProfession = profession || user?.licenseTypes?.[0] || 'RDH';

      const headers = await getAuthHeaders();
      const response = await tempMee.get(`credential-service/credentials`, {
        headers,
        params: {
          profession: userProfession,
          userType: type,
        },
      });

      dispatch({
        type: EDUCATION_ACTION.GET_USER_EDUCATION_SUCCESS,
        payload: response.data[type === 'DNT' ? 'DNT' : userProfession],
      });
    } catch (err) {
      dispatch({ type: EDUCATION_ACTION.GET_USER_EDUCATION_FAILURE, payload: err });
    }
  };

export const updateUserEducation =
  // eslint-disable-next-line camelcase
  ({ years_of_experience, school, graduationDate, education, insurances }) =>
    async (dispatch, getState) => {
      try {
        dispatch({ type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_REQUEST });

        const { users } = getState();
        const currentUser = users?.currentUser || {};
        const { id } = currentUser;

        const payload = {
          school,
          graduationDate,
          // eslint-disable-next-line camelcase
          ...(years_of_experience != null
            ? { professional_info: { exactYearsOfExperience: Number(years_of_experience) } }
            : {}),
        };

        if (getShouldUsingCredentialServiceEndpoint()) {
          await Promise.all([
            // patch yoe, school, graduation date
            patchUser(id, payload),
            // patch specialties & insurance via credentials service
            patchCredentialsV2(
              id,
              currentUser?.user_type,
              {
                ...currentUser?.professional_info?.education,
                ...education,
              },
              insurances,
            ),
          ]);

          dispatch({
            type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_SUCCESS,
            payload: {
              ...currentUser,
              school,
              graduationDate,
              professional_info: {
                ...currentUser?.professional_info,
                ...payload?.professional_info,
                insurances: insurances || [],
                education: {
                  ...currentUser?.professional_info?.education,
                  ...education,
                },
              },
            },
          });
        } else {
          await patchUser(id, payload);
          const response = await patchCredentials(id, education, insurances);

          dispatch({
            type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_SUCCESS,
            payload: {
              ...response.data,
              professional_info: {
                ...response.data?.professional_info,
                ...payload?.professional_info,
              },
            },
          });
        }
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            message: 'User education have been updated successfully.',
          },
        });
      } catch (err) {
        dispatch({ type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_FAILURE, payload: err });
      }
    };

export const fetchStateCredentials = () => async (dispatch) => {
  dispatch({
    type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_REQUEST,
  });

  try {
    const headers = await getAuthHeaders();
    const response = await tempMee.get('users/state-credentials', {
      headers,
    });

    dispatch({ type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_SUCCESS, payload: response.data });
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_FAILURE, payload: err });
  }
};

// eslint-disable-next-line consistent-return
export const updateLicenseCredential = (licenseId, license) => (dispatch) => {
  const validCredentials =
    license?.credentials?.map?.((credential) => {
      const { credential_type: credentialType, entry_field: entryField } = credential || {};

      if (credentialType === 'reference') {
        return {
          ...credential,
          entry_field: {
            // only send references key, anything else is not needed
            references: (entryField.references ?? []).filter(
              ({ officeName, contactEmail, employmentStart, employmentEnd }) =>
                officeName.trim().length > 0 &&
                contactEmail.trim().length > 0 &&
                !!employmentStart &&
                !!employmentEnd,
            ),
          },
        };
      }

      return credential;
    }) || [];

  // since field of Front Desk credential is different from other professions,
  // license profession/sub-profession need to be updated first before updating credentials
  // to avoid wrong input type fields error
  if (validCredentials) {
    // eslint-disable-next-line no-restricted-syntax
    for (const c of validCredentials) {
      const {
        credential_input_type: credentialInputType,
        credential_type: credentialType,
        entry_field: entryField,
        id,
      } = c || {};

      if (c) {
        const isReferenceCredentialValid =
          credentialType === 'reference' &&
          credentialInputType === 'entry_field' &&
          entryField?.references?.length > 0;
        const isLicenseCredentialValid =
          credentialType !== 'reference' &&
          credentialInputType === 'entry_field' &&
          entryField?.license;

        if (isReferenceCredentialValid || isLicenseCredentialValid) {
          if (!id) {
            dispatch(createCredentialEntryType(c, licenseId));
          } else {
            dispatch(updateCredentialEntryType(c, licenseId, id));
          }
        }
      }
    }
  }
};

export const createCredentialEntryType = (data, licId) => async (dispatch, getState) => {
  dispatch({
    type: CREDENTIAL_ACTION.POST_ENTRY_STATE_TYPE_CREDENTIALS_REQUEST,
  });
  try {
    const headers = await getAuthHeaders();
    const currUserId = getState().users.currentUser.id;

    await tempMee.post(`/admin/users/${currUserId}/licenses/${licId}/credentials`, data, {
      headers,
    });
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.POST_ENTRY_STATE_TYPE_CREDENTIALS_FAILURE, payload: err });
  }
};

export const updateCredentialEntryType =
  (data, licId, credentialId) => async (dispatch, getState) => {
    dispatch({
      type: CREDENTIAL_ACTION.PATCH_ENTRY_STATE_TYPE_CREDENTIALS_REQUEST,
    });
    try {
      const headers = await getAuthHeaders();
      const currUserId = getState().users.currentUser.id;

      await tempMee.patch(
        `/admin/users/${currUserId}/licenses/${licId}/credentials/${credentialId}`,
        data,
        {
          headers,
        },
      );
    } catch (err) {
      dispatch({
        type: CREDENTIAL_ACTION.PATCH_ENTRY_STATE_TYPE_CREDENTIALS_FAILURE,
        payload: err,
      });
    }
  };

export const uploadCredential =
  // eslint-disable-next-line consistent-return
  (image, data, licId, credentialId) => async (dispatch, getState) => {
    try {
      dispatch({ type: CREDENTIAL_ACTION.UPLOAD_STATE_CREDENTIALS_REQUEST });

      const currUserId = getState().users.currentUser.id;
      const form = new FormData();
      form.append('image', image);
      const headers = await getAuthHeaders();

      if (data) {
        const credentialUploadData = await tempMee.post(
          `/admin/users/${currUserId}/licenses/${licId}/credentials-upload`,
          data,
          {
            headers,
          },
        );

        // eslint-disable-next-line no-param-reassign
        credentialId = credentialUploadData.data.id;
      }

      const response = await tempMee.patch(
        `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}`,
        form,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      let newCredential = response.data;
      if (getShouldUseProtectedS3Licenses()) {
        newCredential = await dispatch(getProtectedLicenseCredential(licId, newCredential.id));
      }
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          message: 'Credential is uploaded',
        },
      });

      return newCredential;
    } catch (error) {
      dispatch({ type: CREDENTIAL_ACTION.UPLOAD_STATE_CREDENTIALS_FAILURE, payload: error });
    }
  };

export const deleteCredentialUpload = (licId, credentialId) => async (dispatch, getState) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_REQUEST });

    const currUserId = getState().users.currentUser.id;
    const headers = await getAuthHeaders();

    await tempMee.delete(
      `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}`,
      {
        headers: {
          ...headers,
        },
      },
    );
    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        message: 'Credential is deleted',
      },
    });
  } catch (error) {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_FAILURE, payload: error });
  }
};

export const deleteCredentialFile = (licId, credentialId, fileId) => async (dispatch, getState) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_REQUEST });

    const currUserId = getState().users.currentUser.id;
    const headers = await getAuthHeaders();

    await tempMee.delete(
      `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}/${fileId}`,
      {
        headers: {
          ...headers,
        },
      },
    );
    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        message: 'Image is deleted',
      },
    });
  } catch (error) {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_FAILURE, payload: error });
  }
};

export const fetchOfficeCredentials = (userId) => async (dispatch) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_REQUEST });

    const response = await getCredentialsV2(userId, UserType.DNT);

    dispatch({
      type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_SUCCESS,
      payload: {
        softwares: response.data?.softwares || [],
        insurances: response.data?.insurances || [],
      },
    });
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_FAILURE, payload: err });
  }
};

export const fetchUserCredentials = () => async (dispatch, getState) => {
  if (!getShouldUsingCredentialServiceEndpoint()) {
    return;
  }

  try {
    const { users } = getState();
    const currentUser = users?.currentUser || {};

    dispatch({ type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_REQUEST });

    const response = await getCredentialsV2(currentUser?.id, currentUser?.user_type);

    // eslint-disable-next-line camelcase
    const professional_info = {
      ...currentUser.professional_info,
      insurances: response.data?.insurances || [],
      softwares: response.data?.softwares || [],
      education: response?.data?.education,
    };

    dispatch({
      type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_SUCCESS,
      payload: {
        // eslint-disable-next-line camelcase
        professional_info,
      },
    });
  } catch (err) {
    dispatch({
      type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_FAILURE,
      payload: err,
    });
  }
};

export const getProtectedLicenseCredential =
  (licenseId, credentialId) => async (dispatch, getState) => {
    const currUserId = getState().users.currentUser.id;

    const response = await getProtectedProfLicenses(currUserId);
    const licenses = response.data || [];
    const license = licenses.find((item) => item.id === licenseId);

    const credential = license?.credentials?.find?.((item) => item.id === credentialId);
    return credential;
  };

export const fetchAuthorizations = (professionalId) => async (dispatch) => {
  try {
    dispatch({ type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_REQUEST });

    const response = await getAuthorizations(professionalId);

    dispatch({
      type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_SUCCESS,
      payload: response.data?.authorizations || [],
    });
  } catch (err) {
    dispatch({
      type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_FAILURE,
      payload: err,
    });
  }
};

export const createAuthorizationCredential =
  (professionalId, { state, profession }) =>
  async (dispatch) => {
    try {
      dispatch({ type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_REQUEST });

      await createAuthorizationApi(professionalId, { state, profession });

      dispatch({ type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_SUCCESS });
      dispatch({
        type: SHOW_MESSAGE,
        payload: { message: 'Professional Credential successfully.' },
      });

      // refetch list after creation
      dispatch(fetchAuthorizations(professionalId));
    } catch (err) {
      dispatch({
        type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_FAILURE,
        payload: err,
      });
    }
  };

export const deleteAuthorization =
  (professionalId, authorizationId) =>
  async (dispatch) => {
    try {
      dispatch({ type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_REQUEST });

      await deleteAuthorizationApi(professionalId, authorizationId);

      dispatch({ type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_SUCCESS });
      dispatch({
        type: SHOW_MESSAGE,
        payload: { message: 'Professional Credential deleted successfully.' },
      });

      dispatch(fetchAuthorizations(professionalId));
    } catch (err) {
      dispatch({
        type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_FAILURE,
        payload: err,
      });
    }
  };

const formatDate = (d) => {
  if (!d) return null;
  const m = moment.utc(d);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

/* eslint-disable camelcase, no-underscore-dangle */
export const saveCredentials =
  (professionalId, authorization) => async (dispatch) => {
    try {
      const { credentials, id: authorizationId, state, profession } = authorization;
      dispatch({
        type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_REQUEST,
      });

      const promises = credentials.map((cred) => {
        const { type, id, credential, status: credStatus } = cred;

        if (type === 'artifact') {
          const artifactData = cred?.data?.artifact || {};
          const artifactPayload = {
            credential_type: credential,
            issue_date: formatDate(cred.issued_at),
            expire_date: formatDate(cred.expired_at),
            status: credStatus,
            files: artifactData.files || [],
          };
          if (!artifactPayload.issue_date || !artifactPayload.files?.length) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialArtifact(professionalId, id, artifactPayload)
            : createCredentialArtifact(professionalId, authorizationId, artifactPayload);
        }

        if (type === 'number') {
          const numberData = cred?.data?.number || {};
          const numberPayload = {
            credential_type: String(credential || '').replaceAll(String(`${state}_${profession}_`).toLowerCase(), ''),
            number: numberData.credential_number,
            issue_date: formatDate(cred.issued_at),
            expire_date: formatDate(cred.expired_at),
            status: credStatus,
          };
          if (!numberPayload.number || !numberPayload.issue_date || !numberPayload.expire_date) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialNumber(professionalId, id, numberPayload)
            : createCredentialNumber(professionalId, authorizationId, numberPayload);
        }

        if (type === 'reference') {
          const refData = cred?.data?.reference || {};
          const refPayload = {
            credential_type: 'references',
            office_name: refData.office_name,
            contact_email: refData.contact_email,
            start_date: formatDate(refData.start_date),
            end_date: formatDate(refData.end_date),
            status: credStatus,
          };
          if (
            !refPayload.office_name ||
            !refPayload.contact_email ||
            !refPayload.start_date ||
            !refPayload.end_date
          ) {
            return Promise.resolve();
          }
          return id
            ? updateCredentialReference(professionalId, id, refPayload)
            : createCredentialReference(professionalId, authorizationId, refPayload);
        }

        if (type === 'npi') {
          const npiData = cred?.data?.npi || {};
          const npiPayload = {
            credential_type: 'npi_number',
            npi_number: npiData.npi_number,
            status: credStatus,
          };
          if (!npiPayload.npi_number) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialNpi(professionalId, id, npiPayload)
            : createCredentialNpi(professionalId, authorizationId, npiPayload);
        }

        return Promise.resolve();
      });

      await Promise.all(promises);

      dispatch({
        type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_SUCCESS,
      });
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          message: 'Credentials updated successfully.',
        },
      });

      dispatch(fetchAuthorizations(professionalId));
    } catch (err) {
      dispatch({
        type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_FAILURE,
        payload: err,
      });
    }
  };
/* eslint-enable camelcase, no-underscore-dangle */
